import { useAsync } from 'react-use';

import { identityApiRef, useApi } from '@backstage/core-plugin-api';

import { tomtomLabApiRef } from '../api';
import { useEventTheme } from '../context/EventThemeContext';
import { Idea } from '../types';
import { getEffectiveUserRef } from './localDevUser';

const juryMembersAssessmentDisabled = [
  'user:default/frederic.depuydt_tomtom.com',
  'user:default/barbara.lambrecht_tomtom.com',
  'user:default/guillermo.ruiz_tomtom.com',
  'user:default/marcin.bialas_tomtom.com',
  'user:default/marius.swanepoel_tomtom.com',
  'user:default/timothy.grymonpon_tomtom.com',
  'user:default/nathan.gile_tomtom.com',
  'user:default/elias.rosenberg_tomtom.com',
];

export const useFetchJuryMember = () => {
  const identityApi = useApi(identityApiRef);
  const api = useApi(tomtomLabApiRef);

  const { value: userSession } = useAsync(async () =>
    identityApi.getBackstageIdentity(),
  );

  const userRef = getEffectiveUserRef(userSession?.userEntityRef);

  const {
    value: juryMember,
    loading,
    error,
  } = useAsync(async () => {
    if (!userRef) return null;
    try {
      const res = await api.getJuryMemberUserRef(userRef);
      // If your API returns { data: [ ... ] }
      if (Array.isArray(res?.data)) {
        return (
          res.data.find((member: any) => member.userRef === userRef) || null
        );
      }
      // fallback for single object
      return res?.data ?? res;
    } catch (e: any) {
      if (e?.status === 404) return null;
      return null;
    }
  }, [userRef]);

  return { juryMember, loading, error };
};

export const useIsJuryMember = () => {
  const identityApi = useApi(identityApiRef);
  const api = useApi(tomtomLabApiRef);

  const { value: userSession } = useAsync(async () =>
    identityApi.getBackstageIdentity(),
  );

  const userRef = getEffectiveUserRef(userSession?.userEntityRef);

  const { value } = useAsync(async () => {
    if (!userRef) return false;
    try {
      await api.getJuryMemberUserRef(userRef);
      return true;
    } catch (e: any) {
      if (e?.status === 404) return false;
      return false;
    }
  }, [userRef]);

  return value === true;
};

export const useIsJuryMemberAssessmentDisabled = () => {
  const identityApi = useApi(identityApiRef);
  const { value: userSession } = useAsync(async () =>
    identityApi.getBackstageIdentity(),
  );

  const userRef = userSession?.userEntityRef ?? '';

  return juryMembersAssessmentDisabled.includes(userRef);
};

/**
 * Single source of truth for whether the current user may jury-score an idea.
 * Every entry point to the jury assessment (row card, idea dialog, inline form,
 * assessment page) must use this so the juryScores config cannot be bypassed.
 */
export const useCanJuryScore = (
  idea: Pick<Idea, 'status' | 'eventId'> | null | undefined,
) => {
  const { activeEventId, juryVotingEnabled } = useEventTheme();
  const isJuryMember = useIsJuryMember();
  const isAssessmentDisabled = useIsJuryMemberAssessmentDisabled();

  return (
    isJuryMember &&
    !isAssessmentDisabled &&
    juryVotingEnabled &&
    idea?.status === 'readyForJury' &&
    activeEventId !== undefined &&
    idea?.eventId === activeEventId
  );
};
